import { StrictMode, useMemo, useState, type FC } from 'react'
import {
  useCreateCustomField,
  useCustomFieldsCollection,
  useGetCustomFieldTypes,
  useUpdateCustomField,
} from '@opallabs/api'
import { type SimpleWorkspace } from '@opallabs/concepts'
import { type Theme } from '@opallabs/gem'
import { DefaultQueryClientProvider } from '@opallabs/higgs'
import {
  ThemeProvider,
  UserEnvironmentProvider,
  useWorkspace,
  WorkspaceContext,
} from '@opallabs/hooks'
import { Button, Modal, Toast, toast, ToastContainer } from '@opallabs/lapidary'
import { PairingForm } from '../PairingForm'
import { type PairingFormProps, type SerializedData } from '../PairingForm'
import {
  PairingFormProvider,
  prepareEntriesForPersistence,
} from '../PairingForm/useFormState'
import * as styles from './styles'

export type LabelSetPairingModalProps = Pick<
  PairingFormProps,
  | 'canChangeLabelSet'
  | 'customField'
  | 'labelSets'
  | 'options'
  | 'selectedLabelSetId'
> &
  Readonly<{
    onClose?(): void
    onSuccess?(): void
  }>

const Intro: FC = () => (
  <>
    <p>
      Pairing custom fields allows you to apply consistent data across Opal by
      connecting these systems together.
    </p>
    <p>
      Once a custom field is paired with a label set, all values will stay in
      sync, and any edits made to the custom field values will be reflected in
      the label set.
    </p>
    <p>You can turn off the pairing at any time.</p>
  </>
)

function showErrorToast(message: string, title: string) {
  toast((props) => <Toast {...props} message={message} title={title} />, {
    autoClose: 5000,
    closeOnClick: true,
    type: 'error',
    toastId: 'label-pairing-modal-error',
  })
}

function showSuccessToast(message: string, title: string) {
  toast((props) => <Toast {...props} message={message} title={title} />, {
    autoClose: 5000,
    closeOnClick: true,
    type: 'success',
    toastId: 'label-pairing-modal-success',
  })
}

export const LabelSetPairingModal: FC<LabelSetPairingModalProps> = ({
  canChangeLabelSet,
  customField,
  labelSets,
  onClose,
  onSuccess,
  options,
  selectedLabelSetId,
}) => {
  const { uuid: workspaceId } = useWorkspace()
  const { data: fieldTypes = [] } = useGetCustomFieldTypes()
  const { data: existingFields = [] } = useCustomFieldsCollection()
  const existingFieldNames = useMemo(
    () =>
      existingFields
        .filter((field) => !field.isInactive)
        .map((field) => field.name),
    [existingFields]
  )
  const [isValid, setIsValid] = useState(false)
  const [data, setData] = useState<SerializedData>({
    customFieldName: customField.name ?? '',
    labelSetId: selectedLabelSetId ?? '',
    options,
  })
  const createCustomField = useCreateCustomField()
  const updateCustomField = useUpdateCustomField(workspaceId)

  const handleSubmit = () => {
    const serializedOptions = prepareEntriesForPersistence(data.options)
    const baseObject = {
      name: data.customFieldName,
      metadata: { options: serializedOptions },
      pairedLabelSetId: data.labelSetId,
    }

    const labelSet = labelSets.find(({ id }) => id === data.labelSetId)
    if (!labelSet) {
      showErrorToast('Selected label set no longer exists.', 'Error')
      return
    }

    if (customField.id) {
      updateCustomField.mutate(
        {
          ...baseObject,
          fieldId: customField.id,
        },
        {
          onSuccess: () => {
            showSuccessToast(
              `“${data.customFieldName}” has been updated.`,
              'Custom field updated'
            )
            onSuccess?.()
          },
          onError: () => {
            showErrorToast(
              `Updating the label set pairing for the “${data.customFieldName}” custom field failed, please try again.`,
              'Updating custom field pairing failed'
            )
          },
        }
      )
    } else {
      createCustomField.mutate(
        {
          ...baseObject,
          workspaceId: workspaceId,
          fieldTypeId: fieldTypes.find((f) => f.value === 'multiSelect')
            ?.id as string,
        },
        {
          onSuccess: () => {
            showSuccessToast(
              `"${data.customFieldName}" has been created and paired with the "${labelSet.name}" label set.`,
              'Custom field created'
            )
            onSuccess?.()
          },
          onError: (error) => {
            console.error('createCustomField error:', error)
            showErrorToast(
              `Creating a custom field from the "${labelSet.name}" label set failed, please try again.`,
              'Creating custom field failed'
            )
          },
        }
      )
    }
  }

  // @note Using `onClose` for `<Modal.Close>`'s `onClose` prop doesn't handle the case that the user hits the Escape
  // key--the keypress is handled internally in `<Dialog>`, and has to be dealt with at `<Modal.Root>`.
  const handleOpenChange = (open: boolean) => {
    if (!open) onClose?.()
  }

  return (
    <StrictMode>
      <Modal.Root defaultOpen onOpenChange={handleOpenChange}>
        <Modal.Content scrollable={true} css={styles.content}>
          <Modal.Close />

          <Modal.Title>Create custom field from label set</Modal.Title>
          <Modal.MultiStep
            description="Create a multi-select custom field based on a label set"
            finalStepFooterContent={
              <Button
                buttonText="Finish"
                disabled={
                  !isValid ||
                  createCustomField.isLoading ||
                  updateCustomField.isLoading
                }
                name="Create custom field"
                onClick={handleSubmit}
              />
            }
            steps={[
              { bodyContent: <Intro />, title: 'Let’s get started' },
              {
                bodyContent: (
                  <PairingFormProvider
                    canChangeCustomFieldName={true}
                    canChangeLabelSet={canChangeLabelSet}
                    customField={customField}
                    existingFieldNames={existingFieldNames}
                    labelSets={labelSets}
                    onDataChange={setData}
                    onValidityChange={setIsValid}
                    options={data.options}
                    selectedLabelSetId={data.labelSetId}
                  >
                    <PairingForm />
                  </PairingFormProvider>
                ),
                nextDisabled: !isValid,
                title: 'Set up pairing',
              },
              {
                bodyContent: (
                  <PairingFormProvider
                    customField={{ ...customField, name: data.customFieldName }}
                    isReadonly={true}
                    labelSets={labelSets.map((ls) =>
                      ls.id === data.labelSetId
                        ? { ...ls, name: data.customFieldName ?? ls.name }
                        : ls
                    )}
                    options={data.options}
                    selectedLabelSetId={data.labelSetId}
                  >
                    <PairingForm />
                  </PairingFormProvider>
                ),
                title: 'Review output',
              },
            ]}
          />
        </Modal.Content>
      </Modal.Root>
    </StrictMode>
  )
}
LabelSetPairingModal.displayName =
  'AdminConfiguration: LabelSetPairingModal ← CustomFields'

/**
 * `<LabelSetPairingModal>`, but wrapped with all the required providers.
 *
 * @deprecated Only for use in the Marionette codebase. Asgard/Universe views already have the providers set up.
 */
export const QueryableLabelSetPairingModal: FC<
  LabelSetPairingModalProps & {
    theme: Theme
    workspace: SimpleWorkspace
  }
> = ({ theme, workspace, ...props }) => (
  <StrictMode>
    <DefaultQueryClientProvider>
      <WorkspaceContext.Provider value={workspace}>
        <ThemeProvider theme={theme}>
          <UserEnvironmentProvider>
            <LabelSetPairingModal {...props} />
            <ToastContainer />
          </UserEnvironmentProvider>
        </ThemeProvider>
      </WorkspaceContext.Provider>
    </DefaultQueryClientProvider>
  </StrictMode>
)
QueryableLabelSetPairingModal.displayName =
  'AdminConfiguration: QueryableLabelSetPairingModal ← CustomFields'
